import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Box,
  Chip,
  Icon,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { Client } from "../../types/super-admin.types";

export interface BusinessListProps {
  businesses: Client[];
  page: number;
  totalCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onViewDetails: (businessId: string) => void;
  onToggleStatus: (businessId: string, activate: boolean) => Promise<void>;
  isLoading?: boolean;
}

export const BusinessList: React.FC<BusinessListProps> = ({
  businesses = [],
  page = 0,
  totalCount = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  onViewDetails,
  onToggleStatus,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  console.log("businesses", businesses);
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "suspended":
        return "error";
      case "expired":
        return "warning";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(businesses) || businesses.length === 0) {
    return (
      <Paper>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="textSecondary">No businesses found</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                Business Name
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2 }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                Subscription
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                Last Login
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", py: 2 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {businesses.map((business) => (
              <TableRow
                key={business?.id || "unknown"}
                hover
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                    cursor: "pointer",
                  },
                }}
              >
                <TableCell sx={{ py: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: "primary.main",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {business?.business_name?.charAt(0) || "B"}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {business?.business_name || "Unnamed Business"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {business?.id?.slice(0, 8)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {business?.owner?.first_name && business?.owner?.last_name
                        ? `${business.owner.first_name} ${business.owner.last_name}`
                        : "Unknown Owner"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Icon sx={{ fontSize: 14 }}>email</Icon>
                      {business?.owner?.email || "No email provided"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        label={
                          business?.subscription?.plan?.toUpperCase() ||
                          "Unknown"
                        }
                        color="primary"
                        sx={{
                          fontWeight: 500,
                          borderRadius: 1,
                          textTransform: "capitalize",
                        }}
                      />
                      <Chip
                        size="small"
                        label={business?.subscription?.status || "Unknown"}
                        color={getStatusColor(
                          business?.subscription?.status || ""
                        )}
                        sx={{
                          fontWeight: 500,
                          borderRadius: 1,
                          minWidth: 60,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Icon sx={{ fontSize: 14 }}>event</Icon>
                        Expires:{" "}
                        {new Date(
                          business?.subscription?.end_date || ""
                        ).toLocaleDateString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Icon sx={{ fontSize: 14 }}>group</Icon>
                        Users:{" "}
                        {business?.subscription?.features?.max_users || 0}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        label={business?.is_active ? "Active" : "Inactive"}
                        color={business?.is_active ? "success" : "error"}
                        sx={{
                          fontWeight: 500,
                          minWidth: 80,
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Icon sx={{ fontSize: 14 }}>shopping_cart</Icon>
                        Orders: {business?.stats?.total_orders || 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Icon sx={{ fontSize: 14 }}>payments</Icon>
                        Revenue: $
                        {business?.stats?.total_revenue?.toLocaleString() || 0}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography variant="subtitle2">
                      {business?.stats?.last_login
                        ? new Date(
                            business.stats.last_login
                          ).toLocaleDateString()
                        : "Never"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {business?.stats?.last_login
                        ? new Date(
                            business.stats.last_login
                          ).toLocaleTimeString()
                        : "N/A"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        business?.id && onViewDetails(business.id);
                      }}
                      title="Quick View"
                      disabled={!business?.id}
                      sx={{
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                      }}
                    >
                      <Icon fontSize="small">visibility</Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        business?.id &&
                          navigate(`/super/businesses/${business.id}`);
                      }}
                      title="Full Details"
                      disabled={!business?.id}
                      sx={{
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                      }}
                    >
                      <Icon fontSize="small">launch</Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        business?.id &&
                          onToggleStatus(business.id, !business.is_active);
                      }}
                      title={business?.is_active ? "Deactivate" : "Activate"}
                      disabled={!business?.id}
                      color={business?.is_active ? "error" : "success"}
                      sx={{
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                      }}
                    >
                      <Icon fontSize="small">
                        {business?.is_active ? "block" : "check_circle"}
                      </Icon>
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) =>
          onRowsPerPageChange(parseInt(event.target.value, 10))
        }
        sx={{
          borderTop: 1,
          borderColor: "divider",
        }}
      />
    </Paper>
  );
};
